import { useState } from "react";
import { ExternalLink, RefreshCw, ShoppingBag } from "lucide-react";

/**
 * Hero section for product-pool cards. Replaces the animated card
 * cover with a flippable card: the front showcases the product being
 * pooled for, the back reveals the greeting-card template the
 * organiser chose. Same 3/4 aspect ratio so the surrounding layout
 * stays put between pool types.
 *
 * The flip is a CSS 3D transform (preserve-3d + backface-hidden)
 * rather than Framer Motion — it's cheap, needs no refs, and the
 * corner toggle button sits outside the transformed layer so it
 * stays readable on both faces.
 */
interface ProductPoolHeroProps {
  collection: {
    title?: string | null;
    product_name?: string | null;
    product_image?: string | null;
    product_store?: string | null;
    product_url?: string | null;
    product_price?: number | null;
    collection_goal_amount?: number | null;
  };
  templateUrl: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const ProductPoolHero = ({ collection, templateUrl, t }: ProductPoolHeroProps) => {
  const [flipped, setFlipped] = useState(false);
  const name = collection.product_name || collection.title || "";
  const price = collection.product_price ?? collection.collection_goal_amount ?? 0;
  const hasBackSide = !!templateUrl;

  return (
    <div
      className="relative w-full aspect-[3/4]"
      style={{ perspective: "1400px" }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700 ease-in-out"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front — product showcase */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden border border-border/60 shadow-sm bg-card flex flex-col"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          <div className="flex-1 relative bg-gradient-to-b from-muted/40 to-muted/70 flex items-center justify-center p-6">
            {collection.product_image ? (
              <img
                src={collection.product_image}
                alt={name}
                className="w-full h-full object-contain"
              />
            ) : (
              <ShoppingBag size={72} className="text-muted-foreground/50" />
            )}
          </div>
          <div className="border-t border-border/60 bg-background px-5 pt-4 pb-6 space-y-1.5">
            {collection.product_store && (
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                {collection.product_store}
              </p>
            )}
            <h3 className="text-base sm:text-lg font-normal text-foreground leading-tight line-clamp-2 font-serif">
              {name}
            </h3>
            <div className="flex items-center justify-between pt-1.5">
              <p className="text-lg sm:text-xl font-bold text-foreground">
                AED {Number(price).toLocaleString()}
              </p>
              {collection.product_url && (
                <a
                  href={collection.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                >
                  {t("cardView.viewProduct", { defaultValue: "View product" })}
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Back — invitation / greeting card */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden border border-border/60 shadow-sm bg-card"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {hasBackSide ? (
            <div className="relative w-full h-full">
              <img
                src={templateUrl}
                alt={collection.title || ""}
                className="w-full h-full object-cover"
              />
              {collection.title && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5">
                  <p className="text-white text-lg font-serif font-bold leading-tight line-clamp-3 drop-shadow">
                    {collection.title}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              {t("cardView.noCardDesign", { defaultValue: "No card design" })}
            </div>
          )}
        </div>
      </div>

      {/* Flip toggle — lives outside the 3D transformed layer so it
          stays on top in both orientations. Label swaps to match
          what the user will see after the flip. */}
      {hasBackSide && (
        <button
          type="button"
          onClick={() => setFlipped((f) => !f)}
          aria-label={flipped
            ? t("cardView.flipToProduct", { defaultValue: "Show product" })
            : t("cardView.flipToCard", { defaultValue: "Show card" })}
          className="absolute top-3 end-3 h-9 w-9 rounded-full bg-background/95 backdrop-blur border border-border shadow-md flex items-center justify-center text-foreground hover:bg-background transition-colors z-10"
        >
          <RefreshCw size={15} />
        </button>
      )}
    </div>
  );
};

export default ProductPoolHero;
